/* Use the same quote engine as Sutera's product-lp-offer-stack. */
(() => {
  'use strict';
  const cardSelector = '[data-variant-card], [data-lp-variant-card]';
  const flavorSelector = '[data-moon-flavor-option], [data-lp-flavor-option]';
  const money = (cents) => new Intl.NumberFormat('en-US', {
    style: 'currency', currency: window.Shopify?.currency?.active || 'USD',
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(cents / 100);
  const purchaseOptionMode = (root) => {
    const selected = root.querySelector('[data-revlift-subscription-widget] input[name$="selling_plan_choice"]:checked');
    return selected?.value === 'subscription' ? 'subscription' : 'one_time';
  };
  const widgetSavingsPercent = (root, mode) => {
    const badge = root.querySelector(`[data-revlift-option-badge="${mode}"]`);
    const match = badge?.textContent.match(/(\d+(?:\.\d+)?)\s*%/);
    return match ? Number(match[1]) : null;
  };

  function linesForQuantity(root, quantity) {
    const rows = [...root.querySelectorAll('[data-lp-flavor-row]')];
    const selected = (scope) => scope.querySelector(`${flavorSelector.split(', ').map(s => `${s}.is-selected:not(:disabled)`).join(', ')}`)
      || scope.querySelector(`${flavorSelector.split(', ').map(s => `${s}:not(:disabled)`).join(', ')}`);
    return Array.from({ length: quantity }, (_, index) => {
      const flavor = selected(rows[index] || root);
      return {
        id: flavor?.dataset.variantId,
        price: Number(flavor?.dataset.variantPriceCents || 0),
        quantity: 1,
      };
    });
  }

  function render(root, config, refreshWidget = true) {
    const quotes = new Map();
    const cardPriceUpdates = [];
    const activeMode = purchaseOptionMode(root);
    const oneTimeWidgetPercent = widgetSavingsPercent(root, 'one_time');
    const subscriptionWidgetPercent = widgetSavingsPercent(root, 'subscription');
    const subscriptionMultiplier = oneTimeWidgetPercent != null && subscriptionWidgetPercent != null
      && oneTimeWidgetPercent < 100
      ? (100 - subscriptionWidgetPercent) / (100 - oneTimeWidgetPercent)
      : null;
    for (const card of root.querySelectorAll(cardSelector)) {
      const input = card.querySelector('input');
      if (!input || input.disabled) continue;
      const quantity = Math.max(1, Number(input.dataset.quantity || input.value || 1));
      const lines = linesForQuantity(root, quantity);
      if (lines.some(line => !line.id || !line.price)) continue;
      const quote = window.RevLiftPricing.quote(config, lines, { purchaseOptionMode: 'one_time' });
      const savingsQuote = activeMode === 'one_time'
        ? quote
        : window.RevLiftPricing.quote(config, lines, { purchaseOptionMode: activeMode });
      quotes.set(card, quote);
      const displayTotal = activeMode === 'subscription' && subscriptionMultiplier != null
        ? quote.lines.reduce((total, line) => {
          const quantity = Math.max(1, Number(line.quantity || 1));
          const oneTimeUnitTotal = Math.floor(Number(line.discountedTotal || 0) / quantity);
          return total + Math.floor(oneTimeUnitTotal * subscriptionMultiplier) * quantity;
        }, 0)
        : quote.discountedTotal;
      const discounted = displayTotal < quote.regularTotal;
      const price = money(displayTotal);
      const compare = discounted ? money(quote.regularTotal) : '';
      const oneTimePrice = money(quote.discountedTotal);
      const oneTimeCompare = quote.discountedTotal < quote.regularTotal
        ? money(quote.regularTotal)
        : '';
      Object.assign(input.dataset, {
        price: oneTimePrice, comparePrice: oneTimeCompare,
        priceCents: String(quote.discountedTotal),
        compareCents: oneTimeCompare ? String(quote.regularTotal) : '',
        revliftBaseCents: String(quote.regularTotal), revliftBasePrice: money(quote.regularTotal),
      });
      const priceEl = card.querySelector('.offer-box__variant-price');
      if (priceEl) cardPriceUpdates.push({ priceEl, price, compare });
      const note = card.querySelector('[data-revlift-builder-note]');
      if (note) {
        // A mixed-flavor bundle may have different discounts on each line.
        const percents = new Set(savingsQuote.lines.map(line => line.discountPercent));
        let percent = percents.size === 1 ? savingsQuote.discountPercent
          : Math.round((1 - savingsQuote.discountedTotal / savingsQuote.regularTotal) * 10000) / 100;
        if (activeMode === 'subscription' && subscriptionMultiplier != null) {
          const basePercent = quote.lines.length === 1 ? quote.discountPercent
            : Math.round((1 - quote.discountedTotal / quote.regularTotal) * 10000) / 100;
          percent = Math.round((100 - (100 - basePercent) * subscriptionMultiplier) * 10) / 10;
        }
        const hasSavings = displayTotal < quote.regularTotal;
        note.textContent = hasSavings ? `Save ${percent}%` : '';
        note.hidden = !hasSavings;
      }
    }
    const selected = root.querySelector('[data-variant-card].is-selected, [data-lp-variant-card].is-selected');
    const quote = quotes.get(selected);
    if (!quote) return;
    // Purchase Options consumes these totals instead of overriding them with page-only tiers.
    for (const target of [root, root.querySelector('form')].filter(Boolean)) {
      target.dataset.revliftBaseTotalCents = String(quote.regularTotal);
      target.dataset.revliftOneTimeTotalCents = String(quote.discountedTotal);
      target.dataset.revliftCompareTotalCents = String(quote.regularTotal);
    }
    if (!root.querySelector('[data-revlift-subscription-widget]')) {
      const submit = root.querySelector('[data-moon-submit-price], [data-lp-submit-price]');
      const compare = root.querySelector('[data-moon-submit-compare-price], [data-lp-submit-compare-price]');
      if (submit) submit.textContent = money(quote.discountedTotal);
      if (compare) {
        compare.textContent = money(quote.regularTotal);
        compare.hidden = quote.discountedTotal >= quote.regularTotal;
      }
    }
    if (refreshWidget) document.dispatchEvent(new Event('revlift:refresh'));
    cardPriceUpdates.forEach(({ priceEl, price, compare }) => {
      priceEl.replaceChildren(document.createTextNode(price));
      if (compare) {
        const strike = document.createElement('s');
        strike.textContent = compare;
        priceEl.append(' ', strike);
      }
    });
  }

  function start() {
    if (!window.RevLiftPricing) return;
    document.querySelectorAll('[data-offer-box], [data-lp-offer-box]').forEach(root => {
      if (root.dataset.moonPricingReady) return;
      root.dataset.moonPricingReady = 'loading';
      let config;
      const refresh = (refreshWidget = true) => {
        if (config) render(root, config, refreshWidget);
      };
      const refreshPurchaseMode = () => window.setTimeout(() => refresh(false), 100);
      root.addEventListener('change', event => {
        if (event.target.closest(cardSelector)) queueMicrotask(refresh);
        if (event.target.matches('input[name$="selling_plan_choice"]')) refreshPurchaseMode();
      });
      root.addEventListener('click', event => {
        if (event.target.closest(flavorSelector)) queueMicrotask(refresh);
        if (event.target.closest('[data-revlift-option-card]')) refreshPurchaseMode();
      });
      const widgetObserver = new MutationObserver(records => {
        const widget = root.querySelector('[data-revlift-subscription-widget]');
        const widgetUpdated = widget && records.some(record => {
          const target = record.target.nodeType === Node.ELEMENT_NODE ? record.target : record.target.parentElement;
          return target && widget.contains(target);
        });
        if (widgetUpdated && widgetSavingsPercent(root, 'one_time') != null
          && widgetSavingsPercent(root, 'subscription') != null) {
          widgetObserver.disconnect();
          queueMicrotask(refresh);
        }
      });
      widgetObserver.observe(root, { childList: true, subtree: true });
      if (widgetSavingsPercent(root, 'one_time') != null
        && widgetSavingsPercent(root, 'subscription') != null) widgetObserver.disconnect();
      // Like Sutera, load product-scoped config so collection conditions can
      // evaluate the selected variants. LPs can mix several product handles.
      const handles = [...new Set([...root.querySelectorAll(flavorSelector)]
        .map(flavor => flavor.dataset.moonFlavorOption || flavor.dataset.lpFlavorOption)
        .filter(Boolean))];
      Promise.all(['', ...handles].map(handle => window.RevLiftPricing.fetchConfig(handle))).then(configs => {
        config = { ...configs[0], variantCollectionIdsByVariantId: Object.assign({},
          ...configs.map(value => value.variantCollectionIdsByVariantId || {})) };
        root.dataset.moonPricingReady = 'ready';
        refresh();
      }).catch(() => { root.dataset.moonPricingReady = 'unavailable'; });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  document.addEventListener('shopify:section:load', start);
})();
